import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Coordinates } from '../../../../viewer/utilities/math-helpers';
import { Button } from '../../button/button';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Select } from '../../select/select';
import { Stack } from '../../stack/stack';

/**
 * Column mapping configuration for parsing GCP files.
 */
export type GcpColumnMapping = {
  name: number; // Column index for name
  northing: number; // Column index for northing
  easting: number; // Column index for easting
  height: number; // Column index for height
};

type GcpColumnMappingProps = {
  csvColumns: string[];
  columnMapping: GcpColumnMapping;
  onColumnMappingChange: (mapping: GcpColumnMapping) => void;
  validRowCount: number | null;
  showDuplicateNamesWarning: boolean;
  showInvalidCoordinatesWarning: boolean;
  projectionRange: { min: Coordinates; max: Coordinates };
  onImport: () => void;
  onCancel: () => void;
  accumulatedPointsCount: number;
};

export const GcpColumnMapping = ({
  csvColumns,
  columnMapping,
  onColumnMappingChange,
  validRowCount,
  showDuplicateNamesWarning,
  showInvalidCoordinatesWarning,
  projectionRange,
  onImport,
  onCancel,
  accumulatedPointsCount,
}: GcpColumnMappingProps) => {
  const { t } = useTranslation();

  const columnOptions = useMemo(() => {
    return csvColumns.map((col, index) => ({
      id: index.toString(),
      name: col,
    }));
  }, [csvColumns]);

  return (
    <>
      <h4>
        {t('geodataSetup.uploadGcp.heading', { ns: 'cloudProcessing' })} ({accumulatedPointsCount})
      </h4>
      <ColumnMappingContainer>
        <Stack spacing={1.5}>
          <h4>{t('geodataSetup.uploadGcp.mapColumns', { ns: 'cloudProcessing' })}</h4>

          <LabelledContainer
            text={t('geodataSetup.uploadGcp.columns.name', { ns: 'cloudProcessing' })}
          >
            {(formElementId: () => string) => (
              <Select
                id={formElementId()}
                options={columnOptions}
                value={columnMapping.name.toString()}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onColumnMappingChange({
                    ...columnMapping,
                    name: parseInt(e.target.value, 10),
                  })
                }
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            text={t('geodataSetup.uploadGcp.columns.n', { ns: 'cloudProcessing' })}
          >
            {(formElementId: () => string) => (
              <Select
                id={formElementId()}
                options={columnOptions}
                value={columnMapping.northing.toString()}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onColumnMappingChange({
                    ...columnMapping,
                    northing: parseInt(e.target.value, 10),
                  })
                }
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            text={t('geodataSetup.uploadGcp.columns.e', { ns: 'cloudProcessing' })}
          >
            {(formElementId: () => string) => (
              <Select
                id={formElementId()}
                options={columnOptions}
                value={columnMapping.easting.toString()}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onColumnMappingChange({
                    ...columnMapping,
                    easting: parseInt(e.target.value, 10),
                  })
                }
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            text={t('geodataSetup.uploadGcp.columns.h', { ns: 'cloudProcessing' })}
          >
            {(formElementId: () => string) => (
              <Select
                id={formElementId()}
                options={columnOptions}
                value={columnMapping.height.toString()}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onColumnMappingChange({
                    ...columnMapping,
                    height: parseInt(e.target.value, 10),
                  })
                }
              />
            )}
          </LabelledContainer>

          {validRowCount !== null && (
            <InfoBox color={validRowCount > 0 ? 'green' : 'red'}>
              {validRowCount > 0
                ? t('geodataSetup.uploadGcp.foundValidRows', {
                    count: validRowCount,
                    ns: 'cloudProcessing',
                  })
                : t('geodataSetup.uploadGcp.foundNoValidRows', { ns: 'cloudProcessing' })}
            </InfoBox>
          )}

          {showDuplicateNamesWarning && (
            <InfoBox color="yellow" leftIcon={{ icon: 'info-circle' }}>
              {t('geodataSetup.uploadGcp.detectedRowsWithDuplicateNames', {
                ns: 'cloudProcessing',
              })}
            </InfoBox>
          )}

          {showInvalidCoordinatesWarning && (
            <InfoBox color="yellow" leftIcon={{ icon: 'info-circle' }}>
              {t('geodataSetup.uploadGcp.detectedRowsWithInvalidCoordinates', {
                ns: 'cloudProcessing',
              })}
              <br />
              N: {projectionRange.min.northing.toFixed(2)} ~{' '}
              {projectionRange.max.northing.toFixed(2)}
              <br />
              E: {projectionRange.min.easting.toFixed(2)} ~ {projectionRange.max.easting.toFixed(2)}
            </InfoBox>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              color="primary"
              disabled={validRowCount === null || validRowCount === 0}
              variant="contained"
              onClick={onImport}
            >
              {t('geodataSetup.uploadGcp.import', { ns: 'cloudProcessing' })}
            </Button>
            <Button color="secondary" variant="text" onClick={onCancel}>
              {t('cancel', { ns: 'common' })}
            </Button>
          </Stack>
        </Stack>
      </ColumnMappingContainer>
    </>
  );
};

const ColumnMappingContainer = styled.div`
  padding: 2rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
  max-width: 600px;
  margin: 0 auto;
`;
